import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { Model } from '../models/types.ts';
import { paintKey, paintedPiece } from './bake.ts';

/** Steps of the model's own tick tried to find what it moves (12 Hz: a few seconds of sway and flicker). */
const PROBE = [0, 1, 3, 7, 13, 29, 50, 97];

/** An id per function or other object without a uuid, for the material key below. */
const REFS = new WeakMap<object, number>();
let refs = 0;
const ref = (o: object): number => {
  let n = REFS.get(o);
  if (n === undefined) REFS.set(o, (n = ++refs));
  return n;
};

/**
 * A material by what it draws, not by which object it is: models often make a fresh material per piece with the
 * same colour and finish, and two of those can share one draw. Every own property counts (colours by value,
 * textures by uuid, a shader hook by identity), so only materials that render the same come out equal.
 */
const look = (m: THREE.Material): string => {
  const parts: string[] = [m.type];
  for (const [k, v] of Object.entries(m)) {
    if (k === 'uuid' || k === 'name' || k === 'id' || k === 'version' || k === 'userData' || k.startsWith('_')) continue;
    if (v === null || typeof v !== 'object') parts.push(`${k}=${typeof v === 'function' ? `f${ref(v as object)}` : String(v)}`);
    else if ((v as THREE.Color).isColor) parts.push(`${k}=${(v as THREE.Color).getHexString()}`);
    else if ((v as THREE.Vector2).isVector2 || (v as THREE.Euler).isEuler) parts.push(`${k}=${(v as THREE.Vector2).toArray().join(',')}`);
    else parts.push(`${k}=${'uuid' in v ? (v as { uuid: string }).uuid : `o${ref(v as object)}`}`);
  }
  return parts.join(';');
};

/** What a merged mesh must share with the pieces it takes in: else the picture changes. */
const signature = (m: THREE.Mesh): string => {
  const g = m.geometry;
  const attrs = Object.keys(g.attributes)
    .sort()
    .map((k) => {
      const a = g.getAttribute(k);
      return `${k}${a.itemSize}${a.normalized ? 'n' : ''}`;
    })
    .join(',');
  return `${look(m.material as THREE.Material)}|${attrs}|${m.castShadow ? 1 : 0}${m.receiveShadow ? 1 : 0}|${m.renderOrder}|${m.layers.mask}`;
};

/** A mirrored piece (negative scale) baked into the geometry turns its faces inside out: each triangle wound back. */
const unmirror = (g: THREE.BufferGeometry): void => {
  for (const a of Object.values(g.attributes) as THREE.BufferAttribute[]) {
    const n = a.itemSize;
    const arr = a.array;
    for (let t = 0; t + 3 * n <= arr.length; t += 3 * n)
      for (let i = 0; i < n; i++) {
        const x = arr[t + n + i] as number;
        arr[t + n + i] = arr[t + 2 * n + i] as number;
        arr[t + 2 * n + i] = x;
      }
  }
};

/**
 * A model that stands still on the board (a trophy by the keep), drawn in a few calls instead of one per piece: its
 * opaque pieces become one mesh per material. What its tick moves (a bell's swing, a banner in the wind) keeps
 * moving: the pieces under such a part merge in that part's own frame, and the part itself stays as it was.
 * Translucent pieces (their order of drawing is part of the picture) and sprites are left alone. The trophies were ~35 calls each, eight of them most of a big bastion's 390.
 */
export const bakeStill = (model: Model, glows = false): void => {
  const root = model.root;
  // What the tick moves: any node whose own transform or visibility changes over a few seconds of it.
  const nodes: THREE.Object3D[] = [];
  root.traverse((o) => nodes.push(o));
  const pose = (): string[] =>
    nodes.map((o) => {
      if (o.matrixAutoUpdate) o.updateMatrix();
      // A glow's flicker is its opacity: it counts as moving too.
      const op = (o as THREE.Sprite).isSprite ? `o${((o as THREE.Sprite).material as THREE.SpriteMaterial).opacity.toFixed(4)}` : '';
      return `${o.visible ? 1 : 0}${op}${o.matrix.elements.map((v) => v.toFixed(5)).join(',')}`;
    });
  const rest = pose();
  const live = new Set<THREE.Object3D>();
  for (const n of PROBE) {
    model.tick(n);
    pose().forEach((p, i) => p !== rest[i] && live.add(nodes[i] as THREE.Object3D));
  }
  model.tick(0);
  // Where a piece merges: into the nearest part that moves above it (in that part's frame, so it keeps moving with
  // it), else into the model. Null: it moves itself, or hides under something that stays hidden.
  const anchorOf = (o: THREE.Object3D): THREE.Object3D | null => {
    if (live.has(o)) return null;
    for (let x = o.parent; x; x = x.parent) {
      if (live.has(x) || x === root) return x;
      if (!x.visible) return null;
    }
    return null;
  };
  root.updateMatrixWorld(true);
  const ids = new Map<THREE.Object3D, number>();
  const groups = new Map<string, { at: THREE.Object3D; list: THREE.Mesh[]; paint: boolean }>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || (m as unknown as THREE.InstancedMesh).isInstancedMesh || (m as unknown as THREE.SkinnedMesh).isSkinnedMesh) return;
    if (Array.isArray(m.material) || m.material.transparent || Object.keys(m.geometry.morphAttributes).length || m.onBeforeRender !== THREE.Object3D.prototype.onBeforeRender) return;
    const at = m.visible && m.geometry.getAttribute('position') ? anchorOf(m) : null;
    if (!at) return;
    if (!ids.has(at)) ids.set(at, ids.size);
    // Plain paint shares a draw with every piece of the same finish, whatever its colour (painted in the vertices).
    const paint = paintKey(m.material);
    const k = paint ? `${ids.get(at)}|${paint}|${m.castShadow ? 1 : 0}${m.receiveShadow ? 1 : 0}|${m.renderOrder}|${m.layers.mask}` : `${ids.get(at)}|${signature(m)}`;
    const g = groups.get(k) ?? { at, list: [], paint: !!paint };
    g.list.push(m);
    groups.set(k, g);
  });
  const mat = new THREE.Matrix4();
  const toAt = new THREE.Matrix4();
  for (const { at, list, paint } of groups.values()) {
    if (list.length < 2) continue;
    toAt.copy(at.matrixWorld).invert();
    let material = (list[0] as THREE.Mesh).material as THREE.Material;
    const geos = list.map((m) => {
      mat.multiplyMatrices(toAt, m.matrixWorld);
      let g: THREE.BufferGeometry;
      if (paint) {
        const p = paintedPiece(m, mat);
        g = p.geo;
        material = p.material;
      } else {
        g = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone();
        g.clearGroups();
        g.applyMatrix4(mat);
      }
      if (mat.determinant() < 0) unmirror(g);
      return g;
    });
    const merged = mergeGeometries(geos, false);
    for (const g of geos) g.dispose();
    if (!merged) continue;
    const first = list[0] as THREE.Mesh;
    const one = new THREE.Mesh(merged, material);
    one.name = 'still';
    one.castShadow = first.castShadow;
    one.receiveShadow = first.receiveShadow;
    one.renderOrder = first.renderOrder;
    one.layers.mask = first.layers.mask;
    at.add(one);
    for (const m of list) {
      m.removeFromParent();
      m.geometry.dispose();
    }
  }
  // `glows`: the additive glows that stand still leave the scene's drawing for the view's glow layer (one draw for
  // all of them, stillGlows below): a big bastion's trophies drew ~45 glow sprites one call each (2026-10-31).
  if (glows) {
    const list: THREE.Sprite[] = [];
    root.traverse((o) => {
      const sp = o as THREE.Sprite;
      if (!sp.isSprite || !sp.visible) return;
      const m = sp.material as THREE.SpriteMaterial;
      if (m.blending !== THREE.AdditiveBlending || !m.map || anchorOf(sp) !== root) return;
      list.push(sp);
    });
    for (const sp of list) sp.visible = false;
    if (list.length) root.userData.stillGlows = list;
  }
  root.updateMatrixWorld(true);
};

const glowAt = new THREE.Vector3();
const glowScale = new THREE.Vector3();

/**
 * The glows bakeStill took out of a model, for the view's glow layer: where each one is and how it looks now (its
 * material's opacity follows the night like any shared glow). Nothing while the model or a part above them is hidden.
 */
export const stillGlows = (root: THREE.Object3D, add: (x: number, y: number, z: number, color: THREE.Color, size: number, opacity: number) => void): void => {
  const list = root.userData.stillGlows as THREE.Sprite[] | undefined;
  if (!list || !root.visible || !root.parent) return;
  for (const sp of list) {
    let shown = true;
    for (let x = sp.parent; x && x !== root; x = x.parent) if (!x.visible) shown = false;
    if (!shown) continue;
    const m = sp.material as THREE.SpriteMaterial;
    glowAt.setFromMatrixPosition(sp.matrixWorld);
    glowScale.setFromMatrixScale(sp.matrixWorld);
    add(glowAt.x, glowAt.y, glowAt.z, m.color, glowScale.x, m.opacity);
  }
};
